import { animated } from '@react-spring/web';
import type { CancelTokenSource } from 'axios';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import backButtonDark from '@/assets/svg/static/back-button-dark.svg';
import logoFull from '@/assets/svg/static/logo-full.svg';
import {
  DoubleColumnProductList,
  FilterBar,
  SimilarProductImageCarousel,
  type PriceFilterTags,
} from '@/components/features';
import { BottomSheet } from '@/components/layout';
import type { BottomSheetHandle } from '@/components/layout/BottomSheet';
import { PRICE_RANGES } from '@/constants/priceFilterRanges';
import useProductCache from '@/hooks/useProductCache';
import useThemeColor from '@/hooks/useThemeColor';
import {
  createDeeplinkForUrl,
  fetchSimilarProductsWithBox,
  sendFrameSelectionBeacon,
} from '@/services/slaydAPI';
import visionAPI from '@/services/visionAPI';
import useLayoutStore from '@/stores/useLayoutStore';
import type { BoundingBox, BoxDimensions } from '@/types/BoundingBox.types';
import type { Product } from '@/types/Product.types';
import { getMinMaxPriceRange } from '@/utils/formatting';
import SimilarProductsInstallSheet from './SimilarProductsInstallSheet';
import styles from './SimilarProductsPage.module.css';

const CONSTANTS = {
  MAX_PAGE_NUM: 5,
  LIVE_BOX_LABEL: 'Live Box',
} as const;

const FALLBACK_BOX: BoundingBox = {
  label: 'Outfit',
  baseLabel: 'Outfit',
  x: 0.1,
  y: 0.1,
  width: 0.8,
  height: 0.8,
  area: 0.64,
  score: -1,
} as const;

const SimilarProductsPage = (): JSX.Element => {
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [frameInFocus, setFrameInFocus] = useState<number>(0);
  const location = useLocation();
  const navigate = useNavigate();
  useThemeColor('#f9f7f3');
  const [searchParams] = useSearchParams();

  const [boundingBoxes, setBoundingBoxes] = useState<BoundingBox[]>([]);
  const [focusedBoxIndex, setFocusedBoxIndex] = useState<number>(-1);
  const [isGeneratingBoxes, setIsGeneratingBoxes] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [priceFilters, setPriceFilters] =
    useState<PriceFilterTags[]>(PRICE_RANGES);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [liveBox, setLiveBox] = useState<BoundingBox | null>(null);
  const resizeTimeoutRef = useRef<NodeJS.Timeout>(null);
  const cancelTokenRef = useRef<CancelTokenSource | null>(null);
  const keepLoadingRef = useRef<boolean>(false);
  const [isBottomSheetExpanded, setBottomSheetExpanded] = useState(false);
  const setHide = useLayoutStore((state) => state.setHide);
  const bottomSheetRef = useRef<BottomSheetHandle>(null);
  const [onSale, setOnSale] = useState<boolean>(false);
  const [videoId, setVideoId] = useState<string | null>(null);
  const [senderId, setSenderId] = useState<string | null>(null);
  const lastFrameSelectionRef = useRef<string | null>(null);
  const [showInstallSheet, setShowInstallSheet] = useState(false);
  const hasShownInstallPrompt = useRef<boolean>(false);
  const setNavbarVisibility = useLayoutStore(
    (state) => state.setNavbarVisibility
  );
  const [deeplink, setDeeplink] = useState<string | null>(null);
  const [isDeeplinkLoading, setIsDeeplinkLoading] = useState(false);

  const {
    haveProductsInCache,
    getProductsCache,
    setProductsCache,
    haveBoundingBoxesInCache,
    setBoundingBoxCache,
    getBoundingBoxCache,
    getPagesCountCache,
    setPagesCountCache,
    getFocusedBoxIndexCache,
    setFocusedBoxIndexCache,
    getPriceFiltersCache,
    setPriceFiltersCache,
    clearAllCache,
    deleteBoxDataFromCache,
  } = useProductCache();

  const handleBottomSheetExpandedChange = (expanded: boolean): void => {
    setBottomSheetExpanded(expanded);
  };

  useEffect(() => {
    if (showInstallSheet) {
      setNavbarVisibility(false);
    }
    return () => {
      setNavbarVisibility(true);
    };
  }, [showInstallSheet, setNavbarVisibility]);

  // Add scroll handler for the product list
  const handleProductListScroll = useCallback(() => {
    // Only show prompt if bottom sheet is expanded and not already shown
    if (isBottomSheetExpanded && !hasShownInstallPrompt.current) {
      hasShownInstallPrompt.current = true;
      setShowInstallSheet(true);
    }
  }, [isBottomSheetExpanded]);

  useEffect(() => {
    if (imageUrls.length === 0 || deeplink) return;

    const fetchDeepLink = async (): Promise<void> => {
      setIsDeeplinkLoading(true);
      try {
        // Build the web URL with image params
        const params = new URLSearchParams();
        imageUrls.forEach((url) => params.append('image_urls', url));
        const senderId = sessionStorage.getItem('instagramSenderId');
        if (senderId) params.append('sender_id', senderId);
        const webUrl = `https://slayd.in/similar-product?${params.toString()}`;

        const response = await createDeeplinkForUrl({
          url: webUrl,
        });

        setDeeplink(response.deepLink);
      } catch (error) {
        console.error('Failed to create deep link:', error);
        // Will fallback to store URL when user clicks
      } finally {
        setIsDeeplinkLoading(false);
      }
    };

    fetchDeepLink();
  }, [imageUrls, deeplink]);

  useEffect(() => {
    setHide(!isBottomSheetExpanded);
  }, [isBottomSheetExpanded, setHide]);

  // Get image URL from location state or query params
  useEffect(() => {
    if (imageUrls.length !== 0) return;

    if (location && location.state && location.state.imageUrls) {
      setImageUrls(location.state.imageUrls);
    } else {
      const queryParams = new URLSearchParams(window.location.search);
      const multiUrls = queryParams.getAll('image_urls');
      if (multiUrls && multiUrls.length > 0) {
        setImageUrls(multiUrls);
      } else {
        const imgUrl = queryParams.get('image_url');
        if (imgUrl) setImageUrls([imgUrl]);
      }
    }
  }, [location, imageUrls]);

  // Extract videoId and senderId from location state
  useEffect(() => {
    if (location.state?.videoId) {
      setVideoId(location.state.videoId);
    }

    if (location.state?.senderId) {
      setSenderId(location.state.senderId);
    } else {
      setSenderId(searchParams.get('sender_id'));
    }
  }, [location.state, searchParams]);

  // Send frame selection beacon when user comes with senderId and imageUrl
  useEffect(() => {
    if (
      senderId &&
      imageUrls.length > 0 &&
      lastFrameSelectionRef.current !== imageUrls[0]
    ) {
      imageUrls.forEach((imageUrl) => {
        sendFrameSelectionBeacon(videoId || '', imageUrl, senderId);
      });
      lastFrameSelectionRef.current = imageUrls[0];
    }
  }, [senderId, imageUrls, videoId]);

  useEffect(() => {
    if (imageUrls.length === 0) return;

    if (haveBoundingBoxesInCache(frameInFocus)) {
      setBoundingBoxes(getBoundingBoxCache(frameInFocus));
      setFocusedBoxIndex(getFocusedBoxIndexCache(frameInFocus));
      return;
    }

    const setFallbackBox = (): void => {
      const fullImageBox: BoundingBox = FALLBACK_BOX;
      setBoundingBoxCache(frameInFocus, [fullImageBox]);
      setFocusedBoxIndexCache(frameInFocus, 0);
      setBoundingBoxes([fullImageBox]);
      setFocusedBoxIndex(0);
    };

    setIsGeneratingBoxes(true);
    visionAPI.generateBoundingBoxes(
      imageUrls[frameInFocus],
      false,
      (boxes: BoundingBox[], largestBoxIndex: number) => {
        if (boxes.length > 0) {
          setBoundingBoxCache(frameInFocus, boxes);
          setFocusedBoxIndexCache(frameInFocus, largestBoxIndex);
          setBoundingBoxes(boxes);
          setFocusedBoxIndex(largestBoxIndex);
        } else {
          setFallbackBox();
        }

        setIsGeneratingBoxes(false);
      },
      (error) => {
        console.error('Error generating bounding boxes:', error);
        // Handle error case with full image box
        setFallbackBox();
        setIsGeneratingBoxes(false);
      }
    );
  }, [
    imageUrls,
    frameInFocus,
    haveBoundingBoxesInCache,
    setBoundingBoxCache,
    setFocusedBoxIndexCache,
    getFocusedBoxIndexCache,
    getBoundingBoxCache,
  ]);

  // Always derive onSale from URL param
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    setOnSale(searchParams.get('sale') === 'true');
  }, [location.search]);

  // Update fetchProducts to accept and forward sale
  const fetchProducts = useCallback(
    async (
      frameId: number,
      box: BoundingBox,
      priceFiltersLocal: PriceFilterTags[] = PRICE_RANGES,
      pageNum: number = 1,
      sale?: boolean
    ) => {
      if (!imageUrls || imageUrls.length === 0 || !box) return;
      if (pageNum > CONSTANTS.MAX_PAGE_NUM) {
        setHasMore(false);
        return;
      }

      //cancel existing request if any
      if (cancelTokenRef.current) {
        keepLoadingRef.current = true;
        cancelTokenRef.current.cancel('New request started');
        cancelTokenRef.current = null;
      }

      setIsLoading(true);

      const currentLabel = box.label;

      //get products from cache if possible
      if (
        box.label !== CONSTANTS.LIVE_BOX_LABEL &&
        haveProductsInCache(frameId, currentLabel) &&
        pageNum <= getPagesCountCache(frameId, currentLabel)
      ) {
        if (pageNum === CONSTANTS.MAX_PAGE_NUM) setHasMore(false);

        setProducts(getProductsCache(frameId, currentLabel));
        setPriceFilters(getPriceFiltersCache(frameId, currentLabel));
        const pagesInCache = getPagesCountCache(frameId, currentLabel);
        if (pagesInCache < CONSTANTS.MAX_PAGE_NUM) setHasMore(true);
        else setHasMore(false);
        setIsLoading(false);
        keepLoadingRef.current = false;
        return;
      }

      setPriceFilters(priceFiltersLocal);
      const { minPrice, maxPrice } = getMinMaxPriceRange(priceFiltersLocal);

      // Always forward sale param
      const fetchedProducts = await fetchSimilarProductsWithBox({
        imageUrl: imageUrls[frameId],
        pageNum: pageNum,
        box: box,
        minPrice: minPrice,
        maxPrice: maxPrice,
        sale: sale,
        config: {
          cancelToken: cancelTokenRef.current
            ? (cancelTokenRef.current as CancelTokenSource).token
            : undefined,
        },
      });

      if (fetchedProducts) {
        const listOfProducts =
          pageNum === 1
            ? fetchedProducts
            : [...getProductsCache(frameId, currentLabel), ...fetchedProducts];
        setProductsCache(frameId, currentLabel, listOfProducts);
        setPagesCountCache(frameId, currentLabel, pageNum);
        setPriceFiltersCache(frameId, currentLabel, priceFiltersLocal);
        setProducts(listOfProducts);
        if (pageNum >= CONSTANTS.MAX_PAGE_NUM || !fetchedProducts.length) {
          setHasMore(false);
        } else {
          setHasMore(true);
        }
      }

      keepLoadingRef.current = false;
      setIsLoading(false);
      if (cancelTokenRef.current) {
        cancelTokenRef.current = null;
      }
    },
    [
      imageUrls,
      getPagesCountCache,
      setPagesCountCache,
      getProductsCache,
      setProductsCache,
      haveProductsInCache,
      getPriceFiltersCache,
      setPriceFiltersCache,
    ]
  );

  // Fetch products when focused box, priceFilters, or onSale changes
  useEffect(() => {
    if (boundingBoxes.length === 0 || focusedBoxIndex < 0) return;
    setProducts([]);
    const searchParams = new URLSearchParams(location.search);
    const sale = searchParams.get('sale') === 'true';
    // Invalidate cache when sale changes
    deleteBoxDataFromCache(frameInFocus, boundingBoxes[focusedBoxIndex]?.label);
    fetchProducts(
      frameInFocus,
      boundingBoxes[focusedBoxIndex],
      priceFilters,
      1,
      sale
    );
  }, [
    frameInFocus,
    boundingBoxes,
    focusedBoxIndex,
    priceFilters,
    location.search,
    fetchProducts,
    deleteBoxDataFromCache,
  ]);

  /**
   * Handles box focus changes
   * Manages state updates and product fetching when switching between boxes
   *
   * @param {number} index - Index of the box to focus
   */
  const handleBoxFocus = useCallback(
    (index: number) => {
      if (index === focusedBoxIndex && !liveBox) return;
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      if (cancelTokenRef.current) {
        keepLoadingRef.current = true;
        cancelTokenRef.current.cancel('New request started');
        cancelTokenRef.current = null;
      }

      setLiveBox(null);
      // setPriceFilters(PRICE_RANGES);
      if (focusedBoxIndex === index) {
        fetchProducts(frameInFocus, boundingBoxes[focusedBoxIndex]);
      }
      setFocusedBoxIndex(index);
    },
    [boundingBoxes, fetchProducts, focusedBoxIndex, frameInFocus, liveBox]
  );

  const handleSaleToggle = useCallback(() => {
    setProducts([]);
    const searchParams = new URLSearchParams(location.search);
    const isSale = searchParams.get('sale') === 'true';
    if (!isSale) {
      searchParams.set('sale', 'true');
    } else {
      searchParams.delete('sale');
    }
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  }, [location.search, location.pathname, navigate]);

  // Pagination: always use sale param from URL
  const handleLoadMore = useCallback(() => {
    if (isLoading || !hasMore || focusedBoxIndex < 0 || !boundingBoxes.length)
      return;

    const currentBox = liveBox || boundingBoxes[focusedBoxIndex];
    const currentLabel = currentBox.label;
    let currentPage = getPagesCountCache(frameInFocus, currentLabel);
    if (currentPage === 0) currentPage = 1;
    const searchParams = new URLSearchParams(location.search);
    const sale = searchParams.get('sale') === 'true';
    if (currentPage < CONSTANTS.MAX_PAGE_NUM) {
      fetchProducts(
        frameInFocus,
        currentBox,
        priceFilters,
        currentPage + 1,
        sale
      );
    } else {
      setHasMore(false);
    }
  }, [
    fetchProducts,
    hasMore,
    focusedBoxIndex,
    isLoading,
    boundingBoxes,
    liveBox,
    frameInFocus,
    priceFilters,
    getPagesCountCache,
    location.search,
  ]);

  /**
   * Handles box resize start
   * Cleans up existing requests and prepares for new data
   */
  const handleBoxResize = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }
    if (cancelTokenRef.current) {
      keepLoadingRef.current = true;
      cancelTokenRef.current.cancel('New resize request started');
      cancelTokenRef.current = null;
    }
    deleteBoxDataFromCache(frameInFocus, CONSTANTS.LIVE_BOX_LABEL);
    setProducts([]);
    setPriceFilters(PRICE_RANGES);
    setIsLoading(true);
  }, [deleteBoxDataFromCache, frameInFocus]);

  /**
   * Handles box resize completion
   * Updates live box state and fetches new products after resize
   *
   * @param {BoxDimensions} normalizedCoords - New box coordinates
   */
  const handleBoxResizeStop = useCallback(
    (normalizedCoords: BoxDimensions) => {
      // Clear any existing timeout
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      if (cancelTokenRef.current) {
        keepLoadingRef.current = true;
        cancelTokenRef.current.cancel('New resize request started');
        cancelTokenRef.current = null;
      }

      // Store the new box and its products
      const newBox: BoundingBox = {
        ...normalizedCoords,
        label: CONSTANTS.LIVE_BOX_LABEL,
        baseLabel: CONSTANTS.LIVE_BOX_LABEL,
        area: normalizedCoords.width * normalizedCoords.height,
        score: -1,
      };

      // Update the live box with its products
      setLiveBox(newBox);

      // Set a timeout to fetch data
      resizeTimeoutRef.current = setTimeout(() => {
        fetchProducts(frameInFocus, newBox);
      }, 1000);
    },
    [fetchProducts, frameInFocus]
  );

  const handleImageFocusChange = useCallback(
    (index: number) => {
      if (index === frameInFocus) return;

      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      if (cancelTokenRef.current) {
        keepLoadingRef.current = true;
        cancelTokenRef.current.cancel('New request started');
        cancelTokenRef.current = null;
      }
      //saving existing frame state
      if (liveBox) {
        setLiveBox(null);
        deleteBoxDataFromCache(frameInFocus, CONSTANTS.LIVE_BOX_LABEL);
      }

      setFocusedBoxIndexCache(frameInFocus, focusedBoxIndex);
      setPriceFilters(PRICE_RANGES);
      setProducts([]);
      setBoundingBoxes([]);
      setIsLoading(true);
      setFrameInFocus(index);
    },
    [
      frameInFocus,
      focusedBoxIndex,
      liveBox,
      setFocusedBoxIndexCache,
      deleteBoxDataFromCache,
    ]
  );

  // Update handlePriceFilterChange to preserve sale param and let useEffect handle fetch
  const handlePriceFilterChange = useCallback(
    (priceFiltersLocal: PriceFilterTags[]) => {
      if (focusedBoxIndex < 0) return;
      const currentBox = liveBox || boundingBoxes[focusedBoxIndex];
      if (!currentBox) return;

      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      if (cancelTokenRef.current) {
        keepLoadingRef.current = true;
        cancelTokenRef.current.cancel('New request started');
        cancelTokenRef.current = null;
      }

      setProducts([]);
      deleteBoxDataFromCache(frameInFocus, currentBox.label);
      setPriceFilters(priceFiltersLocal);
      // Update URL to preserve sale param
      const { minPrice, maxPrice } = getMinMaxPriceRange(priceFiltersLocal);
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('min', minPrice.toString());
      searchParams.set('max', maxPrice.toString());
      if (onSale) searchParams.set('sale', 'true');
      else searchParams.delete('sale');
      navigate(`${location.pathname}?${searchParams.toString()}`, {
        replace: true,
      });
    },
    [
      boundingBoxes,
      focusedBoxIndex,
      frameInFocus,
      liveBox,
      deleteBoxDataFromCache,
      onSale,
      location.search,
      location.pathname,
      navigate,
    ]
  );

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel('Component unmounted');
        cancelTokenRef.current = null;
      }
    };
  }, []);

  // Reset frame selection tracking when videoId changes
  useEffect(() => {
    lastFrameSelectionRef.current = null;
  }, [videoId]);

  //clear all cache on imageUrls change
  useEffect(() => {
    clearAllCache();
  }, [imageUrls, clearAllCache]);

  const handleBack = (): void => {
    if (window.history.length <= 1) {
      navigate('/');
    } else {
      navigate(-1);
    }
  };

  if (imageUrls.length === 0) {
    return <div className={styles.container} />;
  }

  return (
    <div className={styles.container}>
      <animated.div className={styles.imageDisplayContainer}>
        <SimilarProductImageCarousel
          imageUrls={imageUrls}
          focusedImage={frameInFocus}
          isGeneratingBoxes={isGeneratingBoxes}
          boxes={[...boundingBoxes, ...(liveBox ? [liveBox] : [])]}
          focusedBox={
            isGeneratingBoxes
              ? null
              : liveBox
                ? liveBox
                : boundingBoxes[focusedBoxIndex]
          }
          onBoxFocus={handleBoxFocus}
          onBoxResize={handleBoxResize}
          onBoxResizeStop={handleBoxResizeStop}
          onImageFocusChange={handleImageFocusChange}
        />
      </animated.div>
      <BottomSheet
        ref={bottomSheetRef}
        isExpanded={isBottomSheetExpanded}
        onExpandedChange={handleBottomSheetExpandedChange}
      >
        <div className={styles.filterBarContainer}>
          <FilterBar
            filters={[]} // TO-DO : add filter handling
            priceFilter={priceFilters}
            isSaleSelected={onSale}
            onFilterClick={() => {}}
            onPriceFilterChange={handlePriceFilterChange}
            onSaleToggle={handleSaleToggle}
          />
        </div>
        <DoubleColumnProductList
          products={products}
          className={styles.productListContainer}
          isLoading={isLoading}
          hasMore={hasMore}
          isBottomNavBar={isBottomSheetExpanded}
          onLoadMore={handleLoadMore}
          onScroll={handleProductListScroll}
        />
      </BottomSheet>
      <div className={styles.logoContainer}>
        <img
          src={logoFull}
          alt="Slayd Logo"
          className={styles.logo}
          onClick={() => navigate('/')}
        />
        <button className={styles.backButton} onClick={handleBack}>
          <img src={backButtonDark} alt="Back" className={styles.backIcon} />
        </button>
      </div>

      {showInstallSheet && (
        <SimilarProductsInstallSheet
          isDeeplinkLoading={isDeeplinkLoading}
          deeplink={deeplink}
          imageUrls={imageUrls}
        />
      )}
    </div>
  );
};

export default SimilarProductsPage;
